import type { DocumentTemplate } from '../app-store.js';

type PresetTemplate = Omit<DocumentTemplate, 'id' | 'createdAt' | 'useCount'>;

export const RESTAURANT_TEMPLATES: PresetTemplate[] = [
  {
    businessId: 'preset',
    name: 'Vendor Supply Agreement',
    industry: 'restaurant',
    category: 'Contracts',
    description: 'Agreement with food and beverage suppliers.',
    isPreset: true,
    fields: [
      { id: 'restaurant_name', label: 'Restaurant Name', type: 'text', required: true, width: 'half' },
      { id: 'vendor_name', label: 'Vendor / Supplier Name', type: 'text', required: true, width: 'half' },
      { id: 'products', label: 'Products / Services Supplied', type: 'textarea', required: true, placeholder: 'List all products or categories...' },
      { id: 'delivery_schedule', label: 'Delivery Schedule', type: 'select', required: true, options: ['Daily', 'Twice Weekly', 'Weekly', 'Bi-Weekly', 'On-Demand'], width: 'half' },
      { id: 'payment_terms', label: 'Payment Terms', type: 'select', required: true, options: ['COD', 'Net 7', 'Net 15', 'Net 30', 'Net 60'], width: 'half' },
      { id: 'pricing_notes', label: 'Pricing / Terms Notes', type: 'textarea', required: false, placeholder: 'Describe pricing structure, discounts...' },
      { id: 'start_date', label: 'Agreement Start Date', type: 'date', required: true, width: 'half' },
      { id: 'end_date', label: 'Agreement End Date', type: 'date', required: false, width: 'half' },
      { id: 'restaurant_signature', label: 'Restaurant Signature', type: 'signature', required: true, width: 'half' },
      { id: 'vendor_signature', label: 'Vendor Signature', type: 'signature', required: true, width: 'half' },
    ],
  },
  {
    businessId: 'preset',
    name: 'Employee Availability Form',
    industry: 'restaurant',
    category: 'HR',
    description: 'Staff availability and scheduling preferences.',
    isPreset: true,
    fields: [
      { id: 'employee_name', label: 'Employee Name', type: 'text', required: true, width: 'half' },
      { id: 'position', label: 'Position', type: 'select', required: true, options: ['Server', 'Bartender', 'Host/Hostess', 'Kitchen Staff', 'Chef', 'Busser', 'Dishwasher', 'Manager', 'Other'], width: 'half' },
      { id: 'effective_date', label: 'Effective Date', type: 'date', required: true, width: 'half' },
      { id: 'max_hours', label: 'Max Hours Per Week', type: 'number', required: true, placeholder: '40', width: 'half' },
      { id: 'available_days', label: 'Days Available', type: 'text', required: true, placeholder: 'Mon, Tue, Fri, Sat, Sun', width: 'full' },
      { id: 'preferred_shifts', label: 'Preferred Shifts', type: 'select', required: true, options: ['Morning (6am-2pm)', 'Afternoon (11am-7pm)', 'Evening (4pm-12am)', 'Late Night (8pm-4am)', 'Open (any shift)'], width: 'half' },
      { id: 'not_available', label: 'Days / Times NOT Available', type: 'textarea', required: false, placeholder: 'Every Wednesday, Sunday mornings...' },
      { id: 'notes', label: 'Additional Notes', type: 'textarea', required: false },
      { id: 'employee_signature', label: 'Employee Signature', type: 'signature', required: true, width: 'full' },
    ],
  },
  {
    businessId: 'preset',
    name: 'Catering Agreement',
    industry: 'restaurant',
    category: 'Contracts',
    description: 'Catering event contract with menu and payment details.',
    isPreset: true,
    fields: [
      { id: 'restaurant_name', label: 'Restaurant / Caterer Name', type: 'text', required: true, width: 'half' },
      { id: 'client_name', label: 'Client Name', type: 'text', required: true, width: 'half' },
      { id: 'event_name', label: 'Event Name', type: 'text', required: true, width: 'half' },
      { id: 'event_date', label: 'Event Date', type: 'date', required: true, width: 'half' },
      { id: 'event_location', label: 'Event Location', type: 'text', required: true, width: 'full' },
      { id: 'guest_count', label: 'Guest Count', type: 'number', required: true, placeholder: '100', width: 'half' },
      { id: 'service_style', label: 'Service Style', type: 'select', required: true, options: ['Buffet', 'Plated', 'Cocktail / Passed Apps', 'Family Style', 'Stations', 'Mixed'], width: 'half' },
      { id: 'menu_description', label: 'Menu / Food Description', type: 'textarea', required: true, placeholder: 'Describe the menu items...' },
      { id: 'dietary_notes', label: 'Dietary Restrictions / Special Needs', type: 'text', required: false, placeholder: 'Gluten-free, vegan options...', width: 'full' },
      { id: 'per_person_cost', label: 'Per Person Cost ($)', type: 'currency', required: true, width: 'half' },
      { id: 'total_estimate', label: 'Total Estimated Cost ($)', type: 'currency', required: true, width: 'half' },
      { id: 'deposit_amount', label: 'Deposit Amount ($)', type: 'currency', required: true, width: 'half' },
      { id: 'deposit_due_date', label: 'Deposit Due Date', type: 'date', required: true, width: 'half' },
      { id: 'cancellation_policy', label: 'Cancellation Policy', type: 'textarea', required: true, placeholder: 'Describe cancellation terms...' },
      { id: 'client_signature', label: 'Client Signature', type: 'signature', required: true, width: 'half' },
      { id: 'caterer_signature', label: 'Caterer Signature', type: 'signature', required: true, width: 'half' },
    ],
  },
];

export const GENERAL_TEMPLATES: PresetTemplate[] = [
  {
    businessId: 'preset',
    name: 'Non-Disclosure Agreement (NDA)',
    industry: 'all',
    category: 'Legal',
    description: 'Mutual or one-way NDA for protecting confidential information.',
    isPreset: true,
    fields: [
      { id: 'nda_type', label: 'NDA Type', type: 'select', required: true, options: ['Mutual (Both parties)', 'One-Way (Disclosing party only)'], width: 'half' },
      { id: 'party_one', label: 'Party One (Disclosing)', type: 'text', required: true, width: 'half' },
      { id: 'party_two', label: 'Party Two (Receiving)', type: 'text', required: true, width: 'half' },
      { id: 'purpose', label: 'Purpose of Disclosure', type: 'textarea', required: true, placeholder: 'Describe the business purpose...' },
      { id: 'confidential_info', label: 'Definition of Confidential Information', type: 'textarea', required: true, placeholder: 'Describe what is considered confidential...' },
      { id: 'term_years', label: 'Term (Years)', type: 'number', required: true, placeholder: '2', width: 'half' },
      { id: 'governing_law', label: 'Governing State / Jurisdiction', type: 'text', required: true, placeholder: 'California', width: 'half' },
      { id: 'party_one_signature', label: 'Party One Signature', type: 'signature', required: true, width: 'half' },
      { id: 'party_two_signature', label: 'Party Two Signature', type: 'signature', required: true, width: 'half' },
    ],
  },
  {
    businessId: 'preset',
    name: 'Service Agreement',
    industry: 'all',
    category: 'Contracts',
    description: 'General service contract between a provider and client.',
    isPreset: true,
    fields: [
      { id: 'provider_name', label: 'Service Provider', type: 'text', required: true, width: 'half' },
      { id: 'client_name', label: 'Client Name', type: 'text', required: true, width: 'half' },
      { id: 'services', label: 'Services to be Provided', type: 'textarea', required: true, placeholder: 'Describe all services in detail...' },
      { id: 'start_date', label: 'Start Date', type: 'date', required: true, width: 'half' },
      { id: 'end_date', label: 'End Date', type: 'date', required: false, width: 'half' },
      { id: 'fee', label: 'Fee / Rate', type: 'currency', required: true, width: 'half' },
      { id: 'fee_type', label: 'Fee Type', type: 'select', required: true, options: ['Flat Rate', 'Hourly', 'Daily', 'Weekly', 'Monthly', 'Per Project'], width: 'half' },
      { id: 'payment_due', label: 'Payment Due', type: 'select', required: true, options: ['Upon Completion', 'Net 15', 'Net 30', 'Weekly', 'Monthly', '50% Upfront / 50% on Completion'], width: 'half' },
      { id: 'deliverables', label: 'Deliverables', type: 'textarea', required: false, placeholder: 'List expected deliverables...' },
      { id: 'provider_signature', label: 'Provider Signature', type: 'signature', required: true, width: 'half' },
      { id: 'client_signature', label: 'Client Signature', type: 'signature', required: true, width: 'half' },
    ],
  },
];
